/**
 * Photos a view's page asked to show whole (`coast.showImage`), drawn by the runtime over everything in a browser, where
 * the Coast app's own viewer isn't (docs/views.md#6-the-page-api): one photo at a time on black, fitted to the window,
 * with ✕, and ‹ and › with how many there are when there are several. Escape, a tap beside the photo, or a swipe down
 * closes it; the arrow keys and a swipe sideways page through them.
 */

/** How far a swipe goes, in CSS pixels, before it closes the viewer or turns the page. */
const SWIPE = 70;

export function showImages(urls: string[], start: number) {
  document.querySelector("[data-coast-images]")?.remove();
  let index = start;
  const overlay = element("div", {
    position: "fixed",
    inset: "0",
    zIndex: "2147483647",
    background: "rgba(0, 0, 0, 0.94)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    touchAction: "pinch-zoom",
  });
  overlay.dataset.coastImages = "";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  const image = element("img", { maxWidth: "100%", maxHeight: "100%", objectFit: "contain", userSelect: "none" });
  image.alt = "";
  const close = button("✕", "Close", { top: "16px", right: "16px" });
  const previous = button("‹", "Previous", { left: "16px", top: "50%", transform: "translateY(-50%)" });
  const next = button("›", "Next", { right: "16px", top: "50%", transform: "translateY(-50%)" });
  const count = element("div", {
    position: "absolute",
    bottom: "20px",
    left: "50%",
    transform: "translateX(-50%)",
    color: "rgba(255, 255, 255, 0.8)",
    font: "14px system-ui, sans-serif",
  });
  overlay.append(image, close, previous, next, count);

  const show = () => {
    image.src = urls[index] ?? "";
    const several = urls.length > 1;
    previous.style.display = several && index > 0 ? "" : "none";
    next.style.display = several && index < urls.length - 1 ? "" : "none";
    count.textContent = several ? `${index + 1} of ${urls.length}` : "";
  };
  const page = (by: number) => {
    index = Math.min(Math.max(index + by, 0), urls.length - 1);
    show();
  };
  const done = () => {
    overlay.remove();
    removeEventListener("keydown", keys, true);
  };
  const keys = (event: KeyboardEvent) => {
    if (event.key === "Escape") done();
    else if (event.key === "ArrowLeft") page(-1);
    else if (event.key === "ArrowRight") page(1);
    else return;
    event.preventDefault();
    event.stopPropagation();
  };

  close.onclick = done;
  previous.onclick = () => page(-1);
  next.onclick = () => page(1);
  overlay.onclick = (event) => {
    if (event.target === overlay) done();
  };
  let from: { x: number; y: number } | null = null;
  overlay.addEventListener("touchstart", (event) => {
    const touch = event.touches[0];
    from = event.touches.length === 1 && touch ? { x: touch.clientX, y: touch.clientY } : null;
  });
  overlay.addEventListener("touchend", (event) => {
    const touch = event.changedTouches[0];
    if (!from || !touch) return;
    const dx = touch.clientX - from.x,
      dy = touch.clientY - from.y;
    from = null;
    if (dy > SWIPE && dy > Math.abs(dx)) done();
    else if (Math.abs(dx) > SWIPE && Math.abs(dx) > Math.abs(dy)) page(dx < 0 ? 1 : -1);
  });
  addEventListener("keydown", keys, true);
  document.body.append(overlay);
  show();
  close.focus();
}

function element<K extends keyof HTMLElementTagNameMap>(tag: K, style: Partial<CSSStyleDeclaration>) {
  const made = document.createElement(tag);
  Object.assign(made.style, style);
  return made;
}

function button(label: string, name: string, place: Partial<CSSStyleDeclaration>) {
  const made = element("button", {
    position: "absolute",
    width: "44px",
    height: "44px",
    borderRadius: "22px",
    border: "none",
    background: "rgba(255, 255, 255, 0.16)",
    color: "white",
    font: "22px system-ui, sans-serif",
    cursor: "pointer",
    ...place,
  });
  made.type = "button";
  made.textContent = label;
  made.setAttribute("aria-label", name);
  return made;
}
